import { signal } from "@preact/signals-react";
import { useEffect } from "react";
import { FormattedMessage as FM } from "react-intl";

import Schedule from "./Schedule";
import Sale from "./Sale";
import Payment from "./Payments";
import History from "./History";
import AvailableCalendar from "./AvailableCalendar";
import cn from "classnames";
import useMainStore from "store/mainStore";
import { useContext } from "../store/useContext";
import { layout as layoutSchedule } from "../models/ModelSchedule";
import { XMarkIcon } from "components/Icons";
import { createSale } from "../services/sale";

const currentPage = signal("appointment");
const PAGE_VIEW = {
  appointment: Schedule,
  appointment_calendar: AvailableCalendar,
  services: Sale,
  payments: Payment,
  history: History,
};

function TabItem({ id, disabled, onClick }) {
  let style =
    "border-transparent text-gray-500 border-gray-200 dark:text-gray-400 dark:border-gray-700 hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300";

  if (disabled) {
    style =
      "border-transparent text-gray-400 cursor-not-allowed dark:text-gray-500 cursor-none";
  } else if (currentPage.value === id) {
    style =
      "text-cyan-700 border-b-2 border-cyan-900 active dark:text-gray-500 dark:border-cyan-500";
  }

  return (
    <li className="me-2" id={id}>
      <button
        type="button"
        onClick={() => onClick(id)}
        disabled={disabled}
        className={cn(
          "inline-block pb-3 px-4 pt-4 border-b-2 rounded-t-lg font-semibold",
          style,
        )}
      >
        <FM id={`appointment_scheduling.page.${id}`} />
      </button>
    </li>
  );
}

function TabPages({ onChangeView, onClose }) {
  const { setRecord, record, store, save } = useMainStore();
  const { context, updateContext } = useContext();
  const { profile, currentEvent } = context;
  const PageView = PAGE_VIEW[currentPage.value];

  const pages = [{ id: "appointment", ctxView: layoutSchedule }];

  async function handlePage(pageId) {
    const isSamePage = currentPage.value === pageId;
    if (isSamePage) return;

    if (store && Object.values(store).length > 1 && store.id > 0) {
      await save();
    }
    await setRecordPage(pageId);
    currentPage.value = pageId;
  }

  async function setRecordPage(pageId) {
    let id = null;
    let pageGet = null;
    if (["services", "payments"].includes(pageId)) {
      id = currentEvent?.sale;
      if (!id) {
        id = await createSale(currentEvent.item);
        updateContext({ currentEvent: { ...currentEvent, sale: id } });
      }
      pageGet = "services";
    } else if (pageId === "appointment" && currentEvent.id > 0) {
      id = currentEvent.id;
      pageGet = "appointment";
    }
    if (id && pageGet) {
      const page = pages.find((p) => p.id === pageGet);
      await setRecord(id, page.ctxView.model, page.ctxView);
    }
  }

  function handleChangeView() {
    onChangeView(null, "calendar", null);
  }

  useEffect(() => {
    return () => {
      currentPage.value = "appointment";
    };
  }, []);

  return (
    <div className="min-h-screen">
      <div className="sticky -top-1 z-20 bg-neutral-100 shadow-sm">
        <div className="flex justify-between items-center sticky top-0 text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700 mb-6">
          <ul className="px-4 flex flex-wrap">
            {pages.map((page) => {
              return (
                <TabItem
                  key={page.id}
                  id={page.id}
                  disabled={page.disabled}
                  onClick={handlePage}
                />
              );
            })}
          </ul>
          <button
            type="button"
            className="ml-auto cursor-pointer active:scale-95 mr-2"
            onClick={onClose}
          >
            <XMarkIcon
              width="32"
              className="fill-current text-neutral-500 hover:text-red-600 active:text-blue-300"
            />
          </button>
        </div>
      </div>
      <PageView onChangeView={handleChangeView} />
    </div>
  );
}

export default TabPages;
